import React, { useEffect } from 'react';
import { TrackingProvider, useTrackingContext } from '@givelify/utils';
import {
    Dialog,
    Paper,
    Stack,
    styled,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { CloseCircleIcon } from '../assets';
import { GivelifyButton } from '../button';
import { GivelifyLabel } from '../label';
import { DesignTokens } from '../specify';
import { GivelifyModalProps } from './types';

const StyledDialog = styled(Dialog)<{ fullWidth?: boolean }>(
    ({ fullWidth }) => ({
        '& .MuiBackdrop-root': {
            backgroundColor: DesignTokens.color.overlayModal,
        },
        '& .MuiPaper-root': {
            margin: fullWidth ? 0 : 16,
        },
    }),
);

const StyledPaperNoPadding = styled(Paper)({
    borderRadius: DesignTokens.measurement.borderRadiusL,
    backgroundColor: DesignTokens.color.backgroundPrimary,
    boxShadow: DesignTokens.shadow.shadowHigh,
});

const StyledPaper = styled(StyledPaperNoPadding)({
    padding: DesignTokens.measurement.fontDefaultSize,
});

const StyledAnchorBottomPaper = styled(StyledPaper)({
    '&.MuiDialog-paper': {
        position: 'absolute',
        bottom: 0,
        left: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        margin: '0 !important',
        width: '100vw',
        maxWidth: '100vw',
    },
});

export const GivelifyAnchorBottomPaper = StyledAnchorBottomPaper;

export const GivelifyModalDefaultPaper = StyledPaper;

const GivelifyModalInternal: React.FCC<GivelifyModalProps> = ({
    showCloseButton,
    children,
    disableBackdropClose,
    onClose,
    noPadding,
    classes,
    PaperComponent,
    CloseButton,
    name,
    trackCloseButton = true,
    heading,
    trackModalVisit = true,
    trackUnmountEvent = true,
    autoFullscreen,
    fullScreen: fullScreenProp,
    trackingProvider,
    ...props
}) => {
    const { trackOpenCloseEvent } = useTrackingContext();
    const theme = useTheme();
    const autoFullscreenQuery = React.useMemo(
        () => theme.breakpoints.down(autoFullscreen ?? 'xs'),
        [autoFullscreen, theme.breakpoints],
    );
    const autoFullscreenMatch = useMediaQuery(autoFullscreenQuery);
    const isAutoFullscreen =
        autoFullscreen !== undefined ? autoFullscreenMatch : false;
    const fullScreen = fullScreenProp ?? isAutoFullscreen;

    useEffect(() => {
        if (name && trackModalVisit) {
            trackOpenCloseEvent(true, name);

            return () => {
                if (trackUnmountEvent) trackOpenCloseEvent(false, name);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onCloseButtonClick = React.useCallback(() => {
        if (onClose) onClose({}, 'closeButtonClick');
    }, [onClose]);
    const ModalDialog = noPadding ? StyledPaperNoPadding : StyledPaper;
    return (
        <StyledDialog
            {...props}
            PaperComponent={PaperComponent ? PaperComponent : ModalDialog}
            fullScreen={fullScreen}
            onClose={disableBackdropClose ? undefined : onClose}
        >
            {showCloseButton || heading ? (
                <Stack
                    alignItems="center"
                    className={classes?.header}
                    flexDirection="row"
                    justifyContent="end"
                    paddingTop={
                        noPadding ? DesignTokens.measurement.fontDefaultSize : 0
                    }
                    paddingX={
                        noPadding ? DesignTokens.measurement.fontDefaultSize : 0
                    }
                    zIndex={1}
                >
                    {heading ? (
                        <GivelifyLabel
                            marginRight="auto"
                            text={heading}
                            variant="heading3S"
                        />
                    ) : null}
                    {showCloseButton ? (
                        <GivelifyButton
                            aria-label="Close Dialog"
                            className={classes?.closeButton}
                            name="Close"
                            onClick={onCloseButtonClick}
                            size="small"
                            track={trackCloseButton}
                            variant="icon"
                        >
                            {CloseButton ?? (
                                <CloseCircleIcon
                                    className={classes?.closeIcon}
                                />
                            )}
                        </GivelifyButton>
                    ) : null}
                </Stack>
            ) : null}
            {children}
        </StyledDialog>
    );
};

export const GivelifyModal: React.FCC<GivelifyModalProps> = (props) => {
    if (!props.open) return null;
    if (props.trackingProvider === null) {
        return <GivelifyModalInternal {...props} />;
    }
    const TrackingProviderWrapper = props.trackingProvider
        ? props.trackingProvider
        : TrackingProvider;
    return (
        <TrackingProviderWrapper
            pageName={props.name}
            trackPageVisit={false}
            viewType="modal"
        >
            <GivelifyModalInternal {...props} />
        </TrackingProviderWrapper>
    );
};
